import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";

import ExpandableSection from "@/components/job/ExpandableSection";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep, maxInputNumber } from "@/constants/inputs";
import { cn } from "@/lib/utils";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { getFloat } from "@/utils/mappings";

type DesignConditionsProps = {
  temperatureUnitsOptions: { title: string; value: string }[];
};

const cardName = "designConditions";

const designConditionsSchema = z.object({
  design_pressure: z.union([
    z.number().gte(-15).lt(maxInputNumber),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  design_temperature: z.union([
    z.number().lte(maxInputNumber),
    z.string().refine((value) => value === "" || !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  unit_for_design_temperature: z.string().min(1, "Required"),
});

type DesignConditionsSchema = z.infer<typeof designConditionsSchema>;

export function DesignConditions({ temperatureUnitsOptions }: DesignConditionsProps) {
  const { sections, toggleExpandableSection, setDesignConditions } = useCUMechanicalJobStore(
    useShallow((state) => ({
      sections: state.sections,
      toggleExpandableSection: state.toggleExpandableSection,
      setDesignConditions: state.setDesignConditions,
    }))
  );

  const savedDesignConditions = sections.designConditions;
  const [pendingData, setPendingData] = useState<DesignConditionsSchema | null>(null);
  const [percentIncrease, setPercentIncrease] = useState<string>("");
  const form = useForm<DesignConditionsSchema>({
    resolver: zodResolver(designConditionsSchema),
    values: {
      design_pressure: savedDesignConditions.data.design_pressure ?? "",
      design_temperature: savedDesignConditions.data.design_temperature ?? "",
      unit_for_design_temperature: savedDesignConditions.data.unit_for_design_temperature ?? "",
    },
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  useEffect(() => {
    const savedPercent = savedDesignConditions.data.injection_pressure_pct_increase;
    setPercentIncrease(savedPercent == null ? "" : String(savedPercent));
  }, [savedDesignConditions.data.injection_pressure_pct_increase]);

  const handleSaveDesignConditions = (data: DesignConditionsSchema) => {
    // At this point, form validation guarantees unit_for_design_temperature is a non-empty string
    setPendingData({ ...data, unit_for_design_temperature: data.unit_for_design_temperature });
  };

  const basePressure = pendingData ? Number(pendingData.design_pressure) : 0;
  const pct = percentIncrease.trim() === "" ? 0 : Number(percentIncrease);
  const newPressure = basePressure * (1 + (isNaN(pct) ? 0 : pct / 100));

  const handleSkip = () => {
    if (!pendingData) return;
    setDesignConditions({ data: { ...pendingData, injection_pressure_pct_increase: null }, status: "done" });
    setPendingData(null);
    setPercentIncrease("");
  };

  const handleConfirm = () => {
    if (!pendingData) return;
    const pctValue = percentIncrease.trim() === "" ? null : isNaN(pct) ? null : pct;
    setDesignConditions({ data: { ...pendingData, injection_pressure_pct_increase: pctValue }, status: "done" });
    setPendingData(null);
    setPercentIncrease("");
  };

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedDesignConditions.status}
      title="Design Conditions"
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(handleSaveDesignConditions)} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Design pressure"
              type="number"
              step={inputStep}
              error={errors.design_pressure?.message}
              {...register("design_pressure", { setValueAs: getFloat })}
              endAdornment={
                <Typography variant="text-small" weight="regular" className="text-gray-400">
                  psi
                </Typography>
              }
            />
            <TextInput
              className="flex-1 basis-full md:basis-56"
              label="Design temperature"
              type="number"
              step={inputStep}
              error={errors.design_temperature?.message}
              {...register("design_temperature", { setValueAs: getFloat })}
            />

            <div className="flex-1 basis-full md:basis-56">
              <FormField
                control={form.control}
                name="unit_for_design_temperature"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>Unit for design temperature</FormLabel>
                      <FormControl>
                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={cn(
                              !field.value && "[&>span]:text-gray-400",
                              errors.unit_for_design_temperature && "border-error-300"
                            )}
                          >
                            <SelectValue placeholder="Unit for design temperature" />
                          </SelectTrigger>
                          <SelectContent>
                            {temperatureUnitsOptions.map((unit) => {
                              return (
                                <SelectItem key={unit.value} value={unit.value}>
                                  {unit.title}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </div>
          </div>
          <Button type="submit" className="self-end">
            Save
          </Button>
        </form>
      </Form>
      <Dialog open={!!pendingData} onOpenChange={() => setPendingData(null)}>
        <DialogTitle className="hidden">Modify Design Pressure</DialogTitle>
        <DialogDescription className="hidden">
          Optionally adjust the design pressure by applying a percentage increase
        </DialogDescription>
        <DialogContent>
          <p className="text-2xl">Modify Design Pressure? (Optional)</p>
          <div className="flex w-[300px] flex-col gap-4 md:w-[400px]">
            <div>
              <Typography variant="text-md" className="text-gray-800" weight="semibold">
                Design Pressure
              </Typography>
              <Typography variant="text-md" className="text-gray-600">
                {isNaN(basePressure) ? "" : basePressure.toFixed(2)} psi
              </Typography>
            </div>
            <TextInput
              label="% Design pressure Increase (Injection Pressure)"
              type="number"
              step={inputStep}
              value={percentIncrease}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPercentIncrease(e.target.value)}
              endAdornment={
                <Typography variant="text-small" className="text-gray-400">
                  %
                </Typography>
              }
            />
            <div>
              <Typography variant="text-md" className="text-gray-800" weight="semibold">
                New Design Pressure
              </Typography>
              <Typography variant="text-md" className="text-gray-600">
                {isNaN(newPressure) ? "" : newPressure.toFixed(2)} psi
              </Typography>
            </div>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleSkip}>
                Skip
              </Button>
              <Button className="flex-1" onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ExpandableSection>
  );
}
